import type { SceneNode } from '@aod/protocol';
// City tab content — population, buildings, doctrine, attack/deploy actions.
import type { CitySuggestion } from '@aod/sim-adapter';
import { Badge } from '@aod/ui/components/ui/badge';
import { Button } from '@aod/ui/components/ui/button';
import { Progress } from '@aod/ui/components/ui/progress';
import { Separator } from '@aod/ui/components/ui/separator';
import { styleForCulture } from '@aod/ui/prefabs';
import {
  Building2,
  Check,
  Crown,
  Gem,
  Radius,
  Shield,
  ShieldCheck,
  Swords,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { CitySummonSection } from '../../champion/CitySummonSection.js';
import { type MessageKey, useT } from '../../i18n/index.js';
import { plural } from '../../i18n/rich.js';
import { cityOwnerId, isNeutralProps } from '../../ownership.js';
import { seaStandingOf } from '../../sea/merchantModel.js';
import { ARCHETYPES, type Archetype } from '../DoctrinePanel/archetypes.js';
import type { BuildQueueItem, BuildingDef } from './BuildingSection.js';
import { BuildingSection } from './BuildingSection.js';
import { DoctrineDisplayLine } from './DoctrineDisplayLine.js';
import { Row } from './InfoPanelShell.js';
import {
  MerchantOfferSection,
  MerchantRoutesSection,
  type SeaOffer,
  type SeaRoute,
} from './MerchantOfferSection.js';
import { ShipyardSection } from './ShipyardSection.js';
import { SpritePreview, cityPreviewUrl } from './SpritePreview.js';
import { SuggestSection } from './SuggestSection.js';

const TIER_NAMES: ReadonlyArray<MessageKey | null> = [
  null,
  'city.tier1',
  'city.tier2',
  'city.tier3',
  'city.tier4',
  'city.tier5',
];

export function CityTabContent({
  city,
  localPlayerIndex,
  matchOver,
  garrisonCount,
  buildings,
  treasuryIndustry,
  onAttack,
  onDeploy,
  onStartBuilding,
  onSetDoctrine,
  onAcceptMerchant,
  onDeclineMerchant,
  suggestions,
  onApplySuggestion,
}: {
  city: SceneNode;
  localPlayerIndex: number;
  matchOver: boolean;
  garrisonCount: number;
  buildings: ReadonlyArray<BuildingDef>;
  treasuryIndustry: number;
  onAttack: (cityId: string) => void;
  onDeploy: (cityId: string) => void;
  onStartBuilding: (cityId: string, buildingId: string) => void;
  onSetDoctrine: (cityId: string, archetype: Archetype) => void;
  /** Take the offer a foreign house has made to this harbour. */
  onAcceptMerchant: (cityId: string) => void;
  /** Refuse it. */
  onDeclineMerchant: (cityId: string) => void;
  /** Spec §8 suggest mode — top build/train ideas for the local seat. */
  suggestions: readonly CitySuggestion[];
  onApplySuggestion: (idea: CitySuggestion) => void;
}) {
  const t = useT();
  const tier = (city.props?.tier as number | undefined) ?? 1;
  const neutral = isNeutralProps(city.props);
  const ownerId = cityOwnerId(city.props) ?? 0;
  const population = (city.props?.population as number | undefined) ?? 0;
  const influenceRadius = (city.props?.influenceRadius as number | undefined) ?? 0;
  const hp = (city.props?.hp as number | undefined) ?? 0;
  const hpMax = (city.props?.hpMax as number | undefined) ?? 0;
  const hpPct = hpMax > 0 ? Math.round((hp / hpMax) * 100) : 0;
  const isOwn = !neutral && ownerId === localPlayerIndex;
  const seaOffer = city.props?.seaOffer as SeaOffer | undefined;
  const seaRoutes = (city.props?.seaRoutes as SeaRoute[] | undefined) ?? [];
  // How this owner stands with the houses (null when the sea is not open).
  const seaStanding = seaStandingOf(city.props);
  const color = city.visual?.material?.color ?? '#5ab1ff';
  const tierKey = TIER_NAMES[tier];
  const tierName = tierKey ? t(tierKey) : t('city.tierN', { tier });
  const previewUrl = cityPreviewUrl(city);

  const completedBuildings = (city.props?.completedBuildings as string[] | undefined) ?? [];
  const buildingProgress = city.props?.buildingProgress as string | undefined;
  const buildQueue = city.props?.buildQueue as BuildQueueItem[] | undefined;
  const progressTicks = (city.props?.buildingProgressTicks as number | undefined) ?? 0;
  const progressTotal = (city.props?.buildingProgressTotal as number | undefined) ?? 0;
  const progressPct = progressTotal > 0 ? Math.round((progressTicks / progressTotal) * 100) : 0;

  // The wasm emits the doctrine as flat props (`archetype`, `alignment`);
  // the nested `cityDoctrine` shape is accepted for older snapshots.
  const cityDoctrine = city.props?.cityDoctrine as
    | { archetype: Archetype | null; has_artifact_bonus: boolean }
    | undefined;
  const doctrineArchetype =
    cityDoctrine?.archetype ?? (city.props?.archetype as Archetype | undefined) ?? null;
  const hasArtifactBonus = cityDoctrine?.has_artifact_bonus ?? false;
  const ownerAlignment = city.props?.alignment as 'light' | 'dark' | undefined;
  const culture = styleForCulture(city.props?.culture);
  const cultureKey = culture === 'neutral' ? null : culture;

  return (
    <div className="flex flex-col gap-3">
      {/* Summon first (renders nothing unless relevant): the info panel is only ~410px tall at
          1366x768, so below the portrait or the building list it sat under the fold. */}
      {isOwn && <CitySummonSection city={city} />}

      {previewUrl && <SpritePreview textureUrl={previewUrl} label={tierName} />}

      <div className="flex items-center gap-3">
        <div
          className="flex size-12 items-center justify-center rounded-md"
          style={{
            background: `color-mix(in oklch, ${color} 22%, transparent)`,
            color,
          }}
          aria-hidden
        >
          <Building2 className="size-6" />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <Badge style={{ background: color, color: 'oklch(0.135 0.01 264)' }}>
            {t('city.tierBadge', { name: tierName, tier })}
          </Badge>
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Crown className="size-3" aria-hidden />{' '}
            {neutral ? t('city.neutral') : t('common.player', { player: ownerId + 1 })}
          </p>
        </div>
      </div>

      <Separator />

      {hpMax > 0 && (
        <div className="flex flex-col gap-1.5">
          <Row label={t('common.hpLower')}>
            {hp} / {hpMax}
          </Row>
          <Progress value={hpPct} aria-label={t('city.health')} />
        </div>
      )}

      <DoctrineDisplayLine archetype={doctrineArchetype} hasArtifactBonus={hasArtifactBonus} />

      {isOwn && ownerAlignment && !matchOver && (
        <DoctrineSection
          cityId={city.id}
          alignment={ownerAlignment}
          current={doctrineArchetype}
          onSetDoctrine={onSetDoctrine}
        />
      )}

      <div className="flex flex-col gap-1.5">
        {cultureKey && (
          <div className="flex flex-col gap-0.5" data-testid="city-culture">
            <Row label={t('city.culture')}>{t(`culture.${cultureKey}` as MessageKey)}</Row>
            <p className="text-[11px] text-muted-foreground">
              {t(`culture.${cultureKey}.blurb` as MessageKey)}
            </p>
          </div>
        )}
        <Row label={iconLabel(<Users className="size-3" />, t('city.population'))}>
          {population.toLocaleString()}
        </Row>
        <Row label={iconLabel(<Radius className="size-3" />, t('city.influence'))}>
          {t('city.influenceValue', { radius: influenceRadius })}
        </Row>
        {isOwn && garrisonCount > 0 && (
          <Row label={iconLabel(<ShieldCheck className="size-3" />, t('city.garrison'))}>
            {t(plural('city.garrisonUnits', garrisonCount), { count: garrisonCount })}
          </Row>
        )}
      </div>

      {isOwn && suggestions.length > 0 && (
        <SuggestSection
          suggestions={suggestions}
          buildings={buildings}
          onApply={onApplySuggestion}
        />
      )}

      {isOwn && buildings.length > 0 && (
        <BuildingSection
          cityId={city.id}
          cityTier={tier}
          buildings={buildings}
          completedBuildings={completedBuildings}
          buildingProgress={buildingProgress}
          buildQueue={buildQueue}
          progressPct={progressPct}
          treasuryIndustry={treasuryIndustry}
          onStartBuilding={onStartBuilding}
        />
      )}

      {isOwn && seaOffer && !matchOver && (
        <MerchantOfferSection
          cityId={city.id}
          offer={seaOffer}
          onAccept={onAcceptMerchant}
          onDecline={onDeclineMerchant}
        />
      )}

      {isOwn && (seaRoutes.length > 0 || seaStanding !== null) && (
        <MerchantRoutesSection routes={seaRoutes} standing={seaStanding} />
      )}

      {isOwn && completedBuildings.includes('harbour') && (
        <ShipyardSection
          cityId={city.id}
          completedBuildings={completedBuildings}
          treasuryIndustry={treasuryIndustry}
          onStartBuilding={onStartBuilding}
        />
      )}

      {!isOwn && !matchOver && (
        <>
          <Separator />
          <Button
            type="button"
            variant="destructive"
            className="w-full"
            onClick={() => onAttack(city.id)}
          >
            <Swords className="size-4" aria-hidden /> {t('city.attack')}
          </Button>
        </>
      )}

      {isOwn && garrisonCount > 0 && !matchOver && (
        <>
          <Separator />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => onDeploy(city.id)}
          >
            <Shield className="size-4" aria-hidden /> {t('city.deploy', { count: garrisonCount })}
          </Button>
        </>
      )}
    </div>
  );
}

function iconLabel(icon: ReactNode, text: string) {
  return (
    <span className="inline-flex items-center gap-1">
      {icon}
      {text}
    </span>
  );
}

// ── Doctrine section ───────────────────────────────────────────────────

/** The sim's path membership: each alignment's two primaries can be walked
 * freely; the third available archetype is off-path and needs an artifact. */
const PATH_PRIMARIES: Record<'light' | 'dark', readonly Archetype[]> = {
  light: ['industry', 'faith'],
  dark: ['chaos', 'arcane'],
};
const PATH_AVAILABLE: Record<'light' | 'dark', readonly Archetype[]> = {
  light: ['industry', 'faith', 'science'],
  dark: ['chaos', 'arcane', 'nature'],
};

function DoctrineSection({
  cityId,
  alignment,
  current,
  onSetDoctrine,
}: {
  cityId: string;
  alignment: 'light' | 'dark';
  current: Archetype | null;
  onSetDoctrine: (cityId: string, archetype: Archetype) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('city.doctrine')}
      </p>
      <div className="grid gap-1.5">
        {PATH_AVAILABLE[alignment].map((id) => {
          const meta = ARCHETYPES.find((a) => a.id === id);
          if (!meta) return null;
          const isCurrent = current === id;
          const offPath = !PATH_PRIMARIES[alignment].includes(id);
          const { Icon } = meta;
          return (
            <Button
              key={id}
              type="button"
              variant={isCurrent ? 'secondary' : 'outline'}
              size="sm"
              className="h-9 cursor-pointer justify-start gap-2"
              disabled={isCurrent}
              title={offPath ? t('city.doctrine.needsArtifact') : undefined}
              onClick={() => onSetDoctrine(cityId, id)}
            >
              <Icon className="size-4" aria-hidden />
              <span className="flex-1 text-left">{meta.name}</span>
              {isCurrent ? (
                <Check className="size-4 text-primary" aria-hidden />
              ) : offPath ? (
                <Gem className="size-3.5 text-muted-foreground" aria-hidden />
              ) : null}
            </Button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">{t('city.doctrine.needsArtifactHint')}</p>
    </div>
  );
}
